import { Store } from 'redux'
import { Entity } from '../app/entity'
import SnapshotModel from '../monitor/snapshot/snapshotModel'
import { ApplicationState } from '../redux/ApplicationState'
import { OperationsState } from '../redux/operations/OperationsState'

/**
 * Loader classes like this consilidate all loading operations for a domain
 * concept.
 *
 * They define one (or multiple) load() methods which execute an AJAX call
 * through the Api. You may implement parameter remapping and cache checks in
 * here. For the loaded date there should be a dispatched action through Redux.
 *
 * The Loader also defines a (static) method which handles its own actions and
 * applies the corresponding changes to the global store state.
 */
const Loader = function(store: Store<ApplicationState>, api) {
    this.store = store
    this.api = api

    this.load = parameters => {
        const globalState = this.store.getState()
        if (globalState.operations[+parameters.operation] && globalState.operations[+parameters.operation].isCurrent()) {
            return
        }

        this.api.trigger('XhprofProfilerBundle.Transaction.data', parameters, parameters.operation)
    }

    this.loadGraph = parameters => {
        this.api.triggerContinuously('XhprofPerformanceBundle.Api.operationGraph', parameters, parameters.operation)
    }

    this.loadHistogram = parameters => {
        if (parameters.end) {
            const snapshot = SnapshotModel.fromParameters(parameters)
            parameters = { ...snapshot.toRange(), ...parameters }
        }

        this.api.triggerContinuously('XhprofPerformanceBundle.Api.operationHistogram', parameters, parameters.operation)
    }

    this.loadTaggedVariants = parameters => {
        this.api.triggerContinuously('XhprofProfilerBundle.Transaction.tagged', parameters, parameters.operation)
    }
}

const initialGlobalState: OperationsState = {
    data: {},
    graph: {},
    histogram: {},
    profiles: {},
    tagged: {},
}

Loader.handleAction = (globalState: OperationsState = initialGlobalState, action) => {
    let clonedGlobalState = {}

    switch (action.type) {
        case 'TIDEWAYS_ROUTE':
            return {
                data: Entity.purgeMap(globalState.data),
                graph: Entity.purgeMap(globalState.graph),
                histogram: Entity.purgeMap(globalState.histogram),
                profiles: Entity.purgeMap(globalState.profiles),
                tagged: Entity.purgeMap(globalState.tagged),
            }
        case 'Transaction.data.success':
            clonedGlobalState = { ...globalState.data }
            clonedGlobalState[action.id] = new Entity(action.data.operation, 300)

            return {
                ...globalState,
                data: clonedGlobalState,
            }
        case 'Transaction.data.error':
            clonedGlobalState = { ...globalState.data }
            clonedGlobalState[action.id] = new Entity().setError(action.error)

            return {
                ...globalState,
                data: clonedGlobalState,
            }
        case 'Api.operationGraph.success':
            clonedGlobalState = { ...globalState.graph }
            clonedGlobalState[action.id] = new Entity({ report: action.data.report }, 0)

            return {
                ...globalState,
                graph: clonedGlobalState,
            }
        case 'Api.operationGraph.error':
            clonedGlobalState = { ...globalState.graph }
            clonedGlobalState[action.id] = new Entity().setError(action.error)

            return {
                ...globalState,
                graph: clonedGlobalState,
            }
        case 'Api.operationHistogram.success':
            clonedGlobalState = { ...globalState.histogram }
            clonedGlobalState[action.id] = new Entity({ report: action.data.report }, 0)

            return {
                ...globalState,
                histogram: clonedGlobalState,
            }
        case 'Api.operationHistogram.error':
            clonedGlobalState = { ...globalState.histogram }
            clonedGlobalState[action.id] = new Entity().setError(action.error)

            return {
                ...globalState,
                histogram: clonedGlobalState,
            }
        case 'Transaction.profile.success':
            clonedGlobalState = { ...globalState.profiles }
            clonedGlobalState[action.id] = new Entity({ profiles: action.data.profiles }, 0)

            return {
                ...globalState,
                profiles: clonedGlobalState,
            }
        case 'Transaction.profile.error':
            clonedGlobalState = { ...globalState.profiles }
            clonedGlobalState[action.id] = new Entity().setError(action.error)

            return {
                ...globalState,
                profiles: clonedGlobalState,
            }
        case 'Transaction.tagged.success':
            return {
                ...globalState,
                tagged: {
                    ...globalState.tagged,
                    [action.id]: new Entity(action.data.critical, 300),
                },
            }
        case 'Transaction.tagged.error':
            return {
                ...globalState,
                tagged: {
                    ...globalState.tagged,
                    [action.id]: new Entity().setError(action.error),
                },
            }
    }

    return globalState
}

export default Loader
